import { X, Clock, Tag, FileText, Video, ListChecks, Layers, Link as LinkIcon, ExternalLink, CheckCircle2, Undo2 } from "lucide-react";
import TrainingMarkdown from "@/components/TrainingMarkdown";

const TYPE_META = {
  video: { label: "Video", icon: Video, tone: "bg-blue-50 text-blue-700 border-blue-100" },
  doc: { label: "Doc", icon: FileText, tone: "bg-slate-100 text-slate-700 border-slate-200" },
  checklist: { label: "Checklist", icon: ListChecks, tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  deck: { label: "Deck", icon: Layers, tone: "bg-violet-50 text-violet-700 border-violet-100" },
  pdf: { label: "PDF", icon: FileText, tone: "bg-rose-50 text-rose-700 border-rose-100" },
  external: { label: "External", icon: LinkIcon, tone: "bg-amber-50 text-amber-700 border-amber-100" },
};

const VIDEO_EXTS = [".mp4", ".webm", ".mov", ".m4v"];

function isInlineVideo(url) {
  if (!url) return false;
  const u = url.toLowerCase();
  return VIDEO_EXTS.some((ext) => u.includes(ext));
}

/**
 * Right-side drawer that displays a training module (read-only).
 * Body renders as rich markdown; video URLs play inline.
 */
export default function TrainingModuleReader({
  module,
  onClose,
  completed,
  onMarkComplete,
  onMarkIncomplete,
}) {
  if (!module) return null;
  const meta = TYPE_META[module.content_type] || TYPE_META.doc;
  const Icon = meta.icon;
  const inlineVideo = isInlineVideo(module.content_url);

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      data-testid="training-reader"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-3xl h-full bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600 inline-flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> {module.track}
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-0.5 truncate">{module.title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border font-semibold ${meta.tone}`}>
                <Icon className="w-3 h-3" /> {meta.label}
              </span>
              {module.duration_minutes ? (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {module.duration_minutes} min
                </span>
              ) : null}
              <span className="capitalize">{module.audience}</span>
              <span className="capitalize">· {module.status}</span>
            </div>
          </div>
          <button
            type="button"
            data-testid="training-reader-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 rounded-full w-8 h-8 border border-slate-200 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {module.description && (
            <p className="text-slate-700 leading-relaxed text-[15px]">{module.description}</p>
          )}

          {/* Inline video player when content_url points at an mp4/webm/etc. */}
          {inlineVideo && (
            <div
              className="rounded-2xl overflow-hidden bg-slate-900 shadow-lg shadow-slate-900/10 border border-slate-200"
              data-testid="training-reader-video"
            >
              <video
                src={module.content_url}
                controls
                preload="metadata"
                className="w-full h-auto"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {/* Non-video external link — keep as button. */}
          {module.content_url && !inlineVideo && (
            <a
              href={module.content_url}
              target="_blank"
              rel="noreferrer"
              data-testid="training-reader-external-link"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              Open external resource <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}

          {module.body && <TrainingMarkdown source={module.body} />}

          {!module.body && !module.content_url && (
            <div className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
              No content authored yet.
            </div>
          )}

          {(onMarkComplete || onMarkIncomplete) && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-700">
                {completed ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> Module complete
                  </span>
                ) : (
                  "Mark this module complete once your team has reviewed it."
                )}
              </div>
              {completed ? (
                <button
                  type="button"
                  onClick={onMarkIncomplete}
                  data-testid="training-reader-uncomplete"
                  className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-50"
                >
                  <Undo2 className="w-3 h-3" /> Mark incomplete
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onMarkComplete}
                  data-testid="training-reader-complete"
                  className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-3.5 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                >
                  <CheckCircle2 className="w-3 h-3" /> Mark complete
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

export { TYPE_META };
